import {configureStore} from "@reduxjs/toolkit";
import type {Selector} from "reselect";

import type ServiceApi from "../api";
import type {AppEnvironment} from "../environment";
import accountManagementSlice from "./accountManagementSlice";
import addressSlice from "./addressSlice";
import authSlice from "./authSlice";
import documentsSlice from "./documentsSlice";
import envSlice from "./envSlice";
import idpEnergySlice from "./idpEnergySlice";
import importSlice from "./importSlice";
import locationInfoSlice from "./locationInfoSlice";
import loginSlice from "./loginSlice";
import marketDataInputSlice from "./marketDataInputSlice";
import notificationsSlice from "./notificationsSlice";
import objectInformationSlice from "./objectInformationSlice";
import objectParametersSlice from "./objectParametersSlice";
import pdfPreviewSlice from "./pdfPreviewSlice";
import persistenceSlice from "./persistenceSlice";
import poolStatisticsSlice from "./poolStatisticsSlice";
import searchSnapshotsSlice from "./searchSnapshotsSlice";
import searchValuationsSlice from "./searchValuationsSlice";
import specSlice from "./specSlice";
import themeSlice from "./themeSlice";
import uiPreferencesSlice from "./uiPreferencesSlice";
import valueIndicationSlice from "./valueIndicationSlice";
import valueOrderEntitlementsSlice from "./valueOrderEntitlementsSlice";
import valueOrdersSlice from "./valueOrdersSlice";
import wmtsSlice from "./wmtsSlice";

/**
 * Extra Thunk arguments provided by the Redux store.
 */
export interface RevaExtraArgs {
    env: AppEnvironment;
    serviceApi: ServiceApi;
}

/**
 * Initialize Redux Store.
 * @param env app environment
 * @param serviceApi service API
 */
export default function initStore(env: AppEnvironment, serviceApi: ServiceApi) {
    return configureStore({
        reducer: {
            auth: authSlice,
            accountManagement: accountManagementSlice,
            login: loginSlice,
            theme: themeSlice,
            env: envSlice,
            spec: specSlice,
            address: addressSlice,
            locationInfo: locationInfoSlice,
            persistence: persistenceSlice,
            wmts: wmtsSlice,
            valueIndication: valueIndicationSlice,
            objectParameters: objectParametersSlice,
            objectInformation: objectInformationSlice,
            import: importSlice,
            documents: documentsSlice,
            idpEnergy: idpEnergySlice,
            notifications: notificationsSlice,
            searchValuations: searchValuationsSlice,
            searchSnapshots: searchSnapshotsSlice,
            poolStatistics: poolStatisticsSlice,
            uiPreferences: uiPreferencesSlice,
            valueOrders: valueOrdersSlice,
            valueOrderEntitlements: valueOrderEntitlementsSlice,
            pdfPreview: pdfPreviewSlice,
            marketDataInput: marketDataInputSlice,
        },
        middleware: (getDefaultMiddleware) =>
            getDefaultMiddleware({
                thunk: {
                    extraArgument: {env, serviceApi},
                },
            }),
    });
}

/** Inferred type of the Redux Store */
export type RevaStore = ReturnType<typeof initStore>;
/** Type of the root state of the Redux store (used for all selectors) */
export type RevaRootState = ReturnType<RevaStore["getState"]>;
/** Type of the dispatch (inferred from all Redux reducers) */
export type RevaDispatch = RevaStore["dispatch"];

/** A reselect selector against the Reva root state (optionally with extra positional args). */
export type RevaSelector<Result, Params extends readonly unknown[] = unknown[]> = Selector<
    RevaRootState,
    Result,
    Params
>;

/** A plain selector against the Reva root state (no extra arguments). */
export type PlainRevaSelector<R> = RevaSelector<R, []>;

/** Curried one-argument selector: supply the arg to obtain a plain selector. */
export type CurriedRevaSelector<A, R> = (arg?: A) => PlainRevaSelector<R>;

/** Curried two-argument selector. */
export type CurriedRevaSelector2<A1, A2, R> = (arg1?: A1, arg2?: A2) => PlainRevaSelector<R>;

/** Curried three-argument selector. */
export type CurriedRevaSelector3<A1, A2, A3, R> = (arg1?: A1, arg2?: A2, arg3?: A3) => PlainRevaSelector<R>;
